import React, { useState, useEffect } from "react";

// Check if we're in dev auth bypass mode
const getIsDevBypass = () => {
	if (typeof window === "undefined") {
		// Server-side: check env var
		return ((import.meta as any).env as any).VITE_DEV_AUTH_BYPASS === "true";
	}
	// Client-side: check env var (available via VITE_ prefix)
	return ((import.meta as any).env as any).VITE_DEV_AUTH_BYPASS === "true";
};

// Dynamically import Clerk components only when needed
let ClerkComponents: any = null;

// Lazy load Clerk components on client side
const loadClerkComponents = async () => {
	if (!ClerkComponents && typeof window !== "undefined" && !getIsDevBypass()) {
		const clerk = await import("@clerk/clerk-react");
		ClerkComponents = {
			SignedIn: clerk.SignedIn,
			SignedOut: clerk.SignedOut,
			UserButton: clerk.UserButton,
			RedirectToSignIn: clerk.RedirectToSignIn,
			UserProfile: clerk.UserProfile,
			SignIn: clerk.SignIn,
			SignUp: clerk.SignUp,
		};
	}
};

// Initialize loading
if (typeof window !== "undefined") {
	loadClerkComponents();
}

export function SignedIn({ children }: { children: React.ReactNode }) {
	const [isBypass, setIsBypass] = useState(getIsDevBypass());
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
		setIsBypass(getIsDevBypass());
	}, []);

	// During SSR, render children to avoid hydration mismatch
	if (!isClient) {
		return <>{children}</>;
	}

	if (isBypass) {
		return <>{children}</>;
	}

	// If Clerk components not loaded yet, show nothing or loading state
	if (!ClerkComponents?.SignedIn) {
		return null;
	}

	const ClerkSignedIn = ClerkComponents.SignedIn;
	return <ClerkSignedIn>{children}</ClerkSignedIn>;
}

export function SignedOut({ children }: { children: React.ReactNode }) {
	const [isBypass, setIsBypass] = useState(getIsDevBypass());
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
		setIsBypass(getIsDevBypass());
	}, []);

	if (!isClient) {
		return null;
	}

	if (isBypass) {
		return null;
	}

	if (!ClerkComponents?.SignedOut) {
		return null;
	}

	const ClerkSignedOut = ClerkComponents.SignedOut;
	return <ClerkSignedOut>{children}</ClerkSignedOut>;
}

export function UserButton(props: any) {
	const [isBypass, setIsBypass] = useState(getIsDevBypass());
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
		setIsBypass(getIsDevBypass());
	}, []);

	if (!isClient) {
		return null;
	}

	if (isBypass) {
		return null;
	}

	if (!ClerkComponents?.UserButton) {
		return null;
	}

	const ClerkUserButton = ClerkComponents.UserButton;
	return <ClerkUserButton {...props} />;
}

export function RedirectToSignIn() {
	const [isBypass, setIsBypass] = useState(getIsDevBypass());
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
		setIsBypass(getIsDevBypass());
	}, []);

	if (!isClient) {
		return null;
	}

	if (isBypass) {
		return null;
	}

	if (!ClerkComponents?.RedirectToSignIn) {
		return null;
	}

	const ClerkRedirectToSignIn = ClerkComponents.RedirectToSignIn;
	return <ClerkRedirectToSignIn />;
}

export function UserProfile(props: any) {
	const [isBypass, setIsBypass] = useState(getIsDevBypass());
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
		setIsBypass(getIsDevBypass());
	}, []);

	if (!isClient) {
		return (
			<div className="p-8 text-center">
				<p>Loading...</p>
			</div>
		);
	}

	if (isBypass) {
		return (
			<div className="p-8 text-center">
				<p>User profile is not available in dev bypass mode.</p>
			</div>
		);
	}

	if (!ClerkComponents?.UserProfile) {
		return (
			<div className="p-8 text-center">
				<p>Loading profile...</p>
			</div>
		);
	}

	const ClerkUserProfile = ClerkComponents.UserProfile;
	return <ClerkUserProfile {...props} />;
}

export function SignIn(props: any) {
	const [isBypass, setIsBypass] = useState(getIsDevBypass());
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
		setIsBypass(getIsDevBypass());
	}, []);

	if (!isClient) {
		return (
			<div className="grid h-screen place-items-center">
				<div className="text-center">
					<p className="text-xl">Loading...</p>
				</div>
			</div>
		);
	}

	if (isBypass) {
		return (
			<div className="grid h-screen place-items-center">
				<div className="text-center">
					<p className="text-xl">Sign In is bypassed in dev mode.</p>
					<p className="text-ink-faint">You are automatically signed in.</p>
				</div>
			</div>
		);
	}

	if (!ClerkComponents?.SignIn) {
		return (
			<div className="grid h-screen place-items-center">
				<div className="text-center">
					<p className="text-xl">Loading...</p>
				</div>
			</div>
		);
	}

	const ClerkSignIn = ClerkComponents.SignIn;
	return <ClerkSignIn {...props} />;
}

export function SignUp(props: any) {
	const [isBypass, setIsBypass] = useState(getIsDevBypass());
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
		setIsBypass(getIsDevBypass());
	}, []);

	if (!isClient) {
		return (
			<div className="grid h-screen place-items-center">
				<div className="text-center">
					<p className="text-xl">Loading...</p>
				</div>
			</div>
		);
	}

	if (isBypass) {
		return (
			<div className="grid h-screen place-items-center">
				<div className="text-center">
					<p className="text-xl">Sign Up is bypassed in dev mode.</p>
					<p className="text-ink-faint">You are automatically signed in.</p>
				</div>
			</div>
		);
	}

	if (!ClerkComponents?.SignUp) {
		return (
			<div className="grid h-screen place-items-center">
				<div className="text-center">
					<p className="text-xl">Loading...</p>
				</div>
			</div>
		);
	}

	const ClerkSignUp = ClerkComponents.SignUp;
	return <ClerkSignUp {...props} />;
}
